import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AclModalAdvancedTab } from '@/accessControl/AclModalAdvancedTab.molecule';
import { AclModalCheckAccessTab } from '@/accessControl/AclModalCheckAccessTab.molecule';
import { AclModalLockTab } from '@/accessControl/AclModalLockTab.molecule';
import { AclModalMainTab } from '@/accessControl/AclModalMainTab.molecule';
import { ClosedWhileSavingModal } from '@/accessControl/CloseWhileSavingModal.molecule';
import { GlobalScopeAccessConfirmModal } from '@/accessControl/GlobalScopeAccessConfirmModal.molecule';
import { UnsavedChangesModal } from '@/accessControl/UnsavedChangesModal.molecule';
import {
  ADVANCED_TAB_INDEX,
  BASIC_TAB_INDEX,
  CHECK_ACCESS_TAB_INDEX,
  DEFAULT_ACE,
  DEFAULT_IDENTITY,
  LOCK_TAB_INDEX,
  aclIsModified,
  deDupeAceList,
  determinePermissions,
  getAclSaveToastMessage,
  getAdditionalText,
  getUserDisplayName,
  isGrantingAdditionalReadAccess,
} from '@/accessControl/itemAclModal.utilities';
import { useLockAndVersion } from '@/accessControl/useLockAndVersion.hook';
import { EVERYONE_USERGROUP } from '@/administration/administration.constants';
import { sqWorkbenchStore, sqWorkbookStore, sqWorksheetStore } from '@/core/core.stores';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import { useVersionState } from '@/core/hooks/useVersionState.hook';
import { formatTime, nanosToMillis } from '@/datetime/dateTime.utilities';
import { JOURNAL_OR_REPORT } from '@/homescreen/homescreen.constants';
import { useHomeScreenCurrentTab } from '@/homescreen/homescreen.store';
import { Feature } from '@/licenseManagement/licenseManagement.constants';
import { useHasValidFeature } from '@/licenseManagement/licenseManagement.store';
import { CORPORATE_DRIVE_NAME, DEFAULT_PERMISSIONS, HOME_SCREEN_TABS } from '@/main/app.constants';
import { onHomeScreen } from '@/main/routing.utilities';
import { cancelGroup } from '@/requests/pendingRequests.utilities';
import { emitPermissions } from '@/services/notifier.service';
import { isLockingAndVersioningEnabled } from '@/services/systemConfiguration.utilities';
import { TRACK } from '@/track/track.constants';
import { doTrack } from '@/track/track.service';
import {
  generateSecurityString,
  getItemACL,
  getItemACLDetails,
  getItemName,
  getSwapSourceIdIfSwap,
} from '@/utilities/acl.utilities';
import { SUPPORT_LINK } from '@/utilities/externalLinks';
import { logError } from '@/utilities/logger';
import { formatMessage } from '@/utilities/logger.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { prettyPermissions } from '@/utilities/utilities';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqUserGroupsApi } from '@seeqdev/api/endpoint/UserGroupsApi';
import { sqUsersApi } from '@seeqdev/api/endpoint/UsersApi';
import { SeeqNames } from '@seeqdev/constants';
import { faQuestionCircle } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { ExternalLink, Icon, Modal, Tabs } from '@seeqdev/qomponents';
import type { Ace, Acl, DetailTableAce, Identity } from '@/accessControl/itemAclModal.utilities';
import type { ACLPermissionName } from '@/main/app.constants';
import type { AceInputV1 } from '@seeqdev/api/model/AceInputV1';
import type { AceOutputV1 } from '@seeqdev/api/model/AceOutputV1';
import type { AclInputV1 } from '@seeqdev/api/model/AclInputV1';
import type { IdentityPreviewV1 } from '@seeqdev/api/model/IdentityPreviewV1';
import type { PermissionsV1 } from '@seeqdev/api/model/PermissionsV1';

interface ItemAclModalProps {
  item?: any;
  itemId: string;
  /** IDs for items linked to the original {@link ItemAclModalProps.item} whose ACLs should be updated to match */
  associatedItemIds?: string[];
  closeModal: (securityString?: string) => void;
  itemName?: string;
  workbookId?: string;
  worksheetId?: string;
  // the get links section and corporate message are only included in the modal in the header and home screen actions
  includeLinksAndCorporateMessage?: boolean;
  folderId?: string;
  showOnlyFolderLinks?: boolean;
  isAclModalLocationInHeader?: boolean;
  userHasManagePermission: boolean;
  restrictPermissionsInheritance?: boolean;
  hideLockTab?: boolean;
  // when true, saving ACL changes that newly grant read prompts a confirmation about global search visibility
  itemIsGloballyScoped?: boolean;
}

export const ItemAclModal = ({
  itemId,
  associatedItemIds = [],
  item,
  closeModal,
  itemName,
  workbookId,
  worksheetId,
  includeLinksAndCorporateMessage = false,
  folderId,
  showOnlyFolderLinks,
  isAclModalLocationInHeader = false,
  userHasManagePermission,
  restrictPermissionsInheritance,
  hideLockTab = false,
  itemIsGloballyScoped = false,
}: ItemAclModalProps) => {
  const { t } = useTranslation();

  const versionState = useVersionState();

  const cancellationGroup = `aclUpdate_${itemId}`;

  let everyoneGroup: IdentityPreviewV1 | undefined;
  const {
    ancestors,
    isAnalysis,
    isTopic,

    isLocked,
    lockedMetadata,
    isVantage,
    type: workbookType,
  } = useFluxPaths(sqWorkbookStore, [
    'ancestors',
    'isAnalysis',
    'isTopic',
    'name',
    'isLocked',
    'lockedMetadata',
    'isVantage',
    'type',
  ]);

  const currentTab = useHomeScreenCurrentTab();
  const { stateParams, currentUser } = useFluxPaths(sqWorkbenchStore, ['stateParams', 'currentUser']);
  const WORKBOOK_TYPES = [
    SeeqNames.Types.Analysis,
    SeeqNames.Types.Topic,
    SeeqNames.Types.Vantage,
    SeeqNames.Types.ActionBoard,
  ];
  const itemType = item?.type ?? (itemId === stateParams.workbookId ? workbookType : 'item');
  // Assets are intentionally excluded: parent assets need to be shareable with multiple organizations so that the
  // asset selector can access the parent and the selected child in read-only topics.
  const SINGLE_ORGANIZATION_ACL_RESTRICTED_TYPES = [...WORKBOOK_TYPES, SeeqNames.Types.Folder];
  const isSingleOrganizationAclRestricted =
    SINGLE_ORGANIZATION_ACL_RESTRICTED_TYPES.includes(item?.type) || itemId === stateParams.workbookId;
  const hasLockingFeature = useHasValidFeature(Feature.Locking);
  const isCurrentWorkbookAclTarget = itemId === stateParams.workbookId;
  const lockingAndVersioningAppliesToWorkbook =
    isCurrentWorkbookAclTarget && (isAnalysis || isTopic) && isLockingAndVersioningEnabled();
  const lockingAppliesToVantage = isCurrentWorkbookAclTarget && isVantage && isLockingAndVersioningEnabled();
  const isItemAncestorNotCorporate = !isAclModalLocationInHeader
    ? item?.ancestors?.[0]?.name !== CORPORATE_DRIVE_NAME
    : ancestors[0]?.name !== CORPORATE_DRIVE_NAME;

  const [newAce, setNewAce] = useState(_.clone(DEFAULT_ACE));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [itemAcl, setItemAcl] = useState({} as any);
  const [localAceEntries, setLocalAceEntries] = useState<Ace[]>([]);
  const [displayAceEntries, setDisplayAceEntries] = useState<Ace[]>([]);
  const [isAclFromDatasource, setIsAclFromDatasource] = useState(false);
  const [originalItemAcl, setOriginalItemAcl] = useState<any>(undefined);
  const [itemNameState, setItemNameState] = useState(itemName);
  const [currentTabIndex, setCurrentTabIndex] = useState(BASIC_TAB_INDEX);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [inheritedAcl, setInheritedAcl] = useState<DetailTableAce[]>([]);
  const [detailTableEntries, setDetailTableEntries] = useState<DetailTableAce[]>([]);
  const [originalLocalAces, setOriginalLocalAces] = useState<AceInputV1[] | null>(null);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [showGlobalScopeAccessConfirmModal, setShowGlobalScopeAccessConfirmModal] = useState(false);
  const [showCloseDuringSaveModal, setShowCloseDuringSaveModal] = useState(false);
  const [everyoneDefault, setEveryoneDefault] = useState<IdentityPreviewV1 | undefined>(
    _.clone(DEFAULT_IDENTITY) as IdentityPreviewV1,
  );
  const [understandLockingIsPermanent, setUnderstandLockingIsPermanent] = useState(false);
  const toggleUnderstandLockingIsPermanent = () => {
    setUnderstandLockingIsPermanent(!understandLockingIsPermanent);
  };
  const [lockedByDisplayName, setLockedByDisplayName] = useState<string | undefined>(
    () => lockedMetadata?.lockedByUsername || undefined,
  );
  const lockedTimestamp = lockedMetadata
    ? formatTime(nanosToMillis(lockedMetadata.lockedAt), sqWorksheetStore.timezone)
    : '';
  const [hasMultipleOrganizations, setHasMultipleOrganizations] = useState(false);

  const { lockAndVersion, dismissLockErrors, lockErrors, hasLockingFailedPreviouslyDueToGlobalItems } =
    useLockAndVersion({
      itemId,
      closeModal: () => close(true),
      source: 'shareModal',
    });

  const securityString = useRef<string>(undefined);

  // The organization group present in the ACL when the modal first loaded, if any. Used on save to detect whether
  // this change shared the item with or unshared it from an organization so the success toast can be descriptive.
  const originalOrgIdentity = useRef<Identity | undefined>(undefined);

  /**
   * Updates the itemId to the source item if this item is a swap.
   */
  const setItemIdIfSwap = () =>
    getSwapSourceIdIfSwap(itemId).then((id) => {
      // TODO: CRAB-30435
      /* oxlint-disable-next-line no-param-reassign */
      itemId = id as string;
    });

  useEffect(() => {
    Promise.all([
      sqUserGroupsApi.getUserGroups({ nameSearch: EVERYONE_USERGROUP }).then(({ data }) => {
        everyoneGroup = _.find(data.items, { name: EVERYONE_USERGROUP });
      }),
      setItemIdIfSwap(),
    ]).finally(fetchACLData);
  }, []);

  useEffect(() => {
    if (!isSingleOrganizationAclRestricted) {
      setHasMultipleOrganizations(false);
      return;
    }
    const orgGroupCount = _.uniqBy(
      displayAceEntries.filter((ace) => ace.identity?.isOrganizationGroup),
      (ace) => ace.identity?.id,
    ).length;
    setHasMultipleOrganizations(orgGroupCount > 1);
  }, [displayAceEntries, isSingleOrganizationAclRestricted]);

  useEffect(() => {
    const fallback = lockedMetadata?.lockedByUsername || undefined;
    // Seed with the stored username so the field never renders blank while the lookup is pending.
    setLockedByDisplayName(fallback);
    if (!lockedMetadata?.lockedById) return;

    let active = true;
    sqUsersApi
      .getUser({ id: lockedMetadata.lockedById })
      .then(({ data }) => {
        // Ignore a stale response if the locked user changed before this request resolved.
        if (active) setLockedByDisplayName(getUserDisplayName(data, fallback));
      })
      .catch(() => {
        if (active) setLockedByDisplayName(fallback);
      });
    return () => {
      active = false;
    };
  }, [lockedMetadata?.lockedById, lockedMetadata?.lockedByUsername]);

  /**
   * Fetches data for ACL modal
   */
  const fetchACLData = () => {
    const itemAclPromise = getItemACL(itemId);
    const itemNamePromise = itemName ? Promise.resolve(itemName) : getItemName(itemId);

    Promise.all([itemAclPromise, itemNamePromise])
      .then(([itemAclResponse, itemNameResponse]) => {
        const currentAcl = _.pick(itemAclResponse, ['entries', 'permissionsInheritanceDisabled']);
        setItemAcl(currentAcl);
        const originalOrgEntry = (currentAcl.entries ?? []).find(
          (entry) => entry.identity?.isOrganizationGroup && entry.permissions?.read,
        );
        originalOrgIdentity.current = originalOrgEntry?.identity
          ? {
              id: originalOrgEntry.identity.id ?? '',
              name: originalOrgEntry.identity.name ?? '',
              isOrganizationGroup: true,
            }
          : undefined;
        setIsAclFromDatasource(!!itemAclResponse.permissionsManagedByDatasource);
        setItemNameState(itemNameResponse);
        initializeAceDisplay(currentAcl.entries ?? []);
        setOriginalItemAcl({
          permissionsInheritanceDisabled: !!currentAcl.permissionsInheritanceDisabled,
          entries: _.map(currentAcl.entries, (entry) => ({
            identityId: entry.identity?.id,
            permissions: _.clone(entry.permissions),
          })),
          // set original local aces so we can diff better on changes:
          localEntries: _.chain(currentAcl.entries)
            .reject('origin')
            .reject('role')
            .map((entry) => ({
              identityId: entry.identity?.id,
              permissions: _.clone(entry.permissions),
            }))
            .value(),
        });
        return fetchInheritedAcl().then(() => setLoading(false));
      })
      .catch((error) => {
        errorToast({ httpResponseOrError: error, displayForbidden: true });
        close(false);
      });
  };

  /**
   * When an item is moved to the corporate drive, using the corporate drive message, the permissions get updated to
   * show the new inherited permissions for the Everyone group
   */
  const updatedInheritedPermissionsDisplay = () => {
    const itemAclPromise = getItemACL(itemId);
    let inheritedEveryonePermissions: AceOutputV1 | undefined;

    Promise.all([itemAclPromise])
      .then(([itemAclResponse]) => {
        const currentAcl = _.pick(itemAclResponse, ['entries', 'permissionsInheritanceDisabled']);
        _.filter(currentAcl.entries, (entry) => {
          if (entry.identity?.name === EVERYONE_USERGROUP && entry.origin) {
            inheritedEveryonePermissions = entry;
          }
        });
        itemAcl.entries.push(inheritedEveryonePermissions);
        setItemAcl({ ...itemAcl });
        initializeAceDisplay(itemAcl.entries);
        return fetchInheritedAcl().then(() => setLoading(false));
      })
      .catch((error) => {
        errorToast({ httpResponseOrError: error, displayForbidden: true });
        close(false);
      });
  };

  /**
   * This function sets the localAceEntries and displayAceEntries.
   */
  const initializeAceDisplay = (entries: AceOutputV1[]) => {
    setLocalAceEntries(_.chain(entries).reject('origin').reject('role').value());

    setDisplayAceEntries(
      deDupeAceList(entries, currentTab !== HOME_SCREEN_TABS.CORPORATE, includeLinksAndCorporateMessage),
    );

    setEveryoneDefault(everyoneGroup);
    const addEveryoneDefault: Ace = {
      id: '',
      identity: everyoneGroup || everyoneDefault,
      permissions: {} as PermissionsV1,
      isNew: false,
    };

    if (isItemAncestorNotCorporate && includeLinksAndCorporateMessage && currentTab !== HOME_SCREEN_TABS.CORPORATE) {
      if (
        (everyoneGroup?.isEnabled || everyoneDefault?.isEnabled) &&
        !_.some(entries, (entry) => entry.identity?.id === (everyoneGroup?.id || everyoneDefault?.id))
      ) {
        setLocalAceEntries((prevEntries) => [addEveryoneDefault, ...prevEntries]);
        setDisplayAceEntries((prevEntries) => [addEveryoneDefault, ...prevEntries]);
      }
    }
  };

  /**
   * Saves the changes made to the item ACL, with an option to preview the result of saving the changes without
   * actually persisting them to the backend.
   *
   * @param preview - true to preview the save operation, writing the result to the local view model and
   * leaving the modal open. The backend is not modified when preview is true. The default is false which modifies the
   * backend and closes the modal.
   * @param permInherOverride - the override value for permissions inheritance. If defined, defaults to the
   * itemAcl's permissions inheritance
   * @returns {Promise} a promise that resolved
   */
  const save = (preview = false, permInherOverride?: boolean) => {
    setSaving(!preview);

    let savingLocalAces = localAceEntries;
    if (isItemAncestorNotCorporate && includeLinksAndCorporateMessage && currentTab !== HOME_SCREEN_TABS.CORPORATE) {
      if (!everyoneDefault || everyoneDefault.isEnabled) {
        savingLocalAces = _.filter(savingLocalAces, (ace) => ace.permissions?.read);
      }
    }

    let aceEntries: AceInputV1[] = savingLocalAces.map((ace) => ({
      permissions: ace.permissions ?? {},
      identityId: ace.identity?.id ?? '',
    }));

    const itemType = item?.type ?? (itemId === stateParams.workbookId ? workbookType : undefined);
    const isUser = (ace: Ace) => ace.identity?.type === SeeqNames.Types.User;
    const isGroup = (ace: Ace) => ace.identity?.type === SeeqNames.Types.UserGroup;
    const isEveryone = (ace: Ace) => ace.identity?.name === EVERYONE_USERGROUP;
    const countWhere = (pred: (ace: Ace) => boolean) => savingLocalAces.filter(pred).length;
    const everyoneEntry = savingLocalAces.find(isEveryone);
    doTrack(TRACK.ACCESS_CONTROL, 'save', {
      itemType,
      preview,
      userReadCount: countWhere((a) => isUser(a) && !!a.permissions?.read),
      userWriteCount: countWhere((a) => isUser(a) && !!a.permissions?.write),
      userManageCount: countWhere((a) => isUser(a) && !!a.permissions?.manage),
      groupReadCount: countWhere((a) => isGroup(a) && !isEveryone(a) && !!a.permissions?.read),
      groupWriteCount: countWhere((a) => isGroup(a) && !isEveryone(a) && !!a.permissions?.write),
      groupManageCount: countWhere((a) => isGroup(a) && !isEveryone(a) && !!a.permissions?.manage),
      everyoneRead: !!everyoneEntry?.permissions?.read,
      everyoneWrite: !!everyoneEntry?.permissions?.write,
      everyoneManage: !!everyoneEntry?.permissions?.manage,
    });

    const disablePermissionInheritance = _.isUndefined(permInherOverride)
      ? itemAcl.permissionsInheritanceDisabled
      : permInherOverride;
    const localizeInherited = preview; // Only localize inherited access control entries when previewing

    // if preview is true we need to keep track of the local permissions so that we can revert properly if the user
    // toggles the "disable permission inheritance" checkbox.
    // clicking "disable permission inheritance" adds a local permission for all existing permissions
    // un-checking the checkbox should remove all local permissions that have been added
    // to support this a copy of all "original" local ace entries is stored (and kept in sync upon further updates
    // by the user)
    if (preview) {
      if (disablePermissionInheritance) {
        setOriginalLocalAces(aceEntries);
      } else if (originalLocalAces !== null) {
        aceEntries = originalLocalAces;
      }
    }

    const body: AclInputV1 = {
      entries: aceEntries,
      disablePermissionInheritance,
      preview,
      localizeInherited,
    };

    securityString.current = generateSecurityString(aceEntries);

    return Promise.all(
      [itemId, ...associatedItemIds].map((id) => sqItemsApi.setAcl(body, { id }, { cancellationGroup })),
    )
      .then(([result]) => {
        const { data } = result;
        const { entries, permissionsInheritanceDisabled } = data;
        if (preview) {
          const acl = {
            entries,
            permissionsInheritanceDisabled,
          };

          setItemAcl(acl);
          initializeAceDisplay(acl.entries ?? []);

          if (currentTabIndex === ADVANCED_TAB_INDEX) {
            setDetailsEntries(acl);
          }
        } else {
          emitPermissions(stateParams.workbookId, stateParams.worksheetId, itemId);
          showSaveToast(entries ?? []);
          close(true);
        }
      })
      .catch((error) => {
        errorToast({ httpResponseOrError: error, displayForbidden: true });
      })
      .finally(() => {
        setSaving(false);
      });
  };

  /**
   * Closes the item ACL modal. Prompts the user if they are not saving and there are unsaved changes to give the
   * user the opportunity to save the changes if desired.
   *
   * @param {boolean} forceClose - true if the modal should close, even with pending changes.
   * @param cancelRequest - true if the request should be cancelled.
   */
  const close = (forceClose = false, cancelRequest = false) => {
    doTrack(TRACK.ACCESS_CONTROL, 'close');
    if (!forceClose && aclIsModified(itemAcl, originalItemAcl)) {
      setShowUnsavedChangesModal(true);
    } else {
      closeModal(securityString.current);
      if (cancelRequest) {
        cancelGroup(cancellationGroup);
      }
    }
  };

  /**
   * Adds an ACE entry based on the identity and access level selected.
   * Note: The change is only performed in memory. Changes are persisted when save() is called.
   */
  const addItemAce = () => {
    doTrack(TRACK.ACCESS_CONTROL, 'Add');
    itemAcl.entries.push(_.cloneDeep(_.omit(newAce, 'isNew')));
    setItemAcl({ ...itemAcl });

    initializeAceDisplay(itemAcl.entries);
    setNewAce({ ...DEFAULT_ACE });
    setEveryoneDefault(everyoneDefault);
  };

  /**
   * Toggles permission inheritance and updates the local itemAcl
   */
  const togglePermissionsInheritance = () => {
    setItemAcl({
      ...itemAcl,
      permissionsInheritanceDisabled: !itemAcl.permissionsInheritanceDisabled,
    });
    save(true, !itemAcl.permissionsInheritanceDisabled);
  };

  /**
   * This function is called when the user clicks a checkbox.
   *
   * Only "local" permissions can be set via the ACL Modal - inherited permissions must not be manipulated. Users
   * can however add local permission in addition to existing inherited permissions.
   * This function ensures that the proper local permissions entries are created/updated.
   *
   * @param permission - the name of the permission selected by the user.
   * @param ace - object representing an ace entry
   */
  const setPermissions = (permission: ACLPermissionName, ace: Ace) => {
    const identityId = ace?.identity?.id;
    const isNew = ace?.isNew;
    let toUpdate;

    const getLocalAce = () => {
      const existingLocalAce = _.find(localAceEntries, ['identity.id', identityId]);
      if (existingLocalAce) {
        return existingLocalAce;
      } else {
        const inheritedAceForIdentity = _.find(itemAcl.entries, ['identity.id', identityId]);
        const newLocalAce = _.cloneDeep(_.omit(inheritedAceForIdentity, ['origin', 'role']));
        _.assign(newLocalAce, { permissions: _.clone(DEFAULT_PERMISSIONS) });
        return newLocalAce;
      }
    };

    toUpdate = isNew ? newAce : getLocalAce();
    toUpdate.permissions = determinePermissions(permission, toUpdate.permissions);

    if (!isNew) {
      const updatedItemAcl = { ...itemAcl };
      const updatedLocalAceEntries = [...localAceEntries];
      if (!_.isNil(identityId)) {
        const { finalEntries, finalLocalEntries } = updateLocalAces(
          toUpdate,
          identityId,
          updatedItemAcl,
          updatedLocalAceEntries,
        );
        syncOriginalLocalAces(identityId, toUpdate.permissions);
        setDisplayAceEntries(
          deDupeAceList(
            finalEntries as AceOutputV1[],
            currentTab !== HOME_SCREEN_TABS.CORPORATE,
            includeLinksAndCorporateMessage,
          ),
        );

        const everyoneInUpdatedEntries = _.size(
          _.filter(finalLocalEntries, (entry) => entry.identity?.name === EVERYONE_USERGROUP),
        );
        const everyoneInItemAcl = _.size(
          _.filter(finalEntries, (entry) => entry.identity?.name === EVERYONE_USERGROUP),
        );

        if (
          isItemAncestorNotCorporate &&
          includeLinksAndCorporateMessage &&
          currentTab !== HOME_SCREEN_TABS.CORPORATE
        ) {
          if (
            everyoneDefault?.isEnabled &&
            (toUpdate.identity?.id === everyoneDefault?.id
              ? !toUpdate.permissions.read && displayAceEntries[0].permissions?.read && everyoneInUpdatedEntries === 0
              : !displayAceEntries[0].permissions?.read && everyoneInItemAcl === 0)
          ) {
            setDisplayAceEntries((prevEntries) => [
              {
                id: '',
                identity: everyoneDefault,
                permissions: {},
                isNew: false,
              },
              ...prevEntries,
            ]);
            setLocalAceEntries((prevEntries) => [
              {
                id: '',
                identity: everyoneDefault,
                permissions: {},
                isNew: false,
              },
              ...prevEntries,
            ]);
          }
        }
      }
    } else {
      setNewAce({ ...toUpdate });
    }
  };

  /**
   * To prevent the aclIsModified function to return "false positives" when users check and then uncheck the same
   * permission for which no local entry existed, local entries that are all false are removed from the
   * localAceEntries as well as the itemAcl.entries. This ensures that the "save" button does not show as active if
   * I click and un-click the same permission on an entry that currently only shows inherited permissions.
   *
   * Even if we don't remove any entries, we need to ensure that the items mapping to the local entries have been
   * updated
   *
   * @param toUpdate - the ACE to update
   * @param identityId - the id of the identity (user/group)
   * @param {object} updatedItemAcl - the updated itemAcl
   * @param {object[]} updatedLocalAceEntries - The updated list of local aces
   */
  const updateLocalAces = (
    toUpdate: Ace,
    identityId: string,
    updatedItemAcl: Acl,
    updatedLocalAceEntries: Ace[],
  ): { finalEntries: Ace[]; finalLocalEntries: Ace[] } => {
    const originalLocalIndex = _.findIndex(originalItemAcl.localEntries, {
      identityId: toUpdate.identity?.id,
    });

    if (
      _.isEqual(toUpdate.permissions, DEFAULT_PERMISSIONS) &&
      originalLocalIndex < 0 &&
      toUpdate.identity?.name !== 'Everyone'
    ) {
      // Local entry needs to be removed
      const finalLocalEntries = updatedLocalAceEntries.filter((entry) => entry.identity?.id !== identityId);
      const finalEntries = updatedItemAcl.entries.filter(
        (entry) => _.has(entry, 'origin') || _.has(entry, 'role') || _.get(entry, 'identity.id') !== identityId,
      );
      setLocalAceEntries(finalLocalEntries);
      setItemAcl({ ...updatedItemAcl, entries: finalEntries });
      return { finalEntries, finalLocalEntries };
    } else {
      const finalLocalEntries = [
        ...updatedLocalAceEntries.filter((entry) => entry.identity?.id !== identityId),
        toUpdate,
      ];
      const finalEntries = [...updatedItemAcl.entries.filter((entry) => entry.identity?.id !== identityId), toUpdate];
      setLocalAceEntries(finalLocalEntries);
      setItemAcl({ ...updatedItemAcl, entries: finalEntries });
      return { finalEntries, finalLocalEntries };
    }
  };

  /**
   * We need to be able to toggle between Enable/Disable permission inheritance so we need to keep track of all the
   * user changes to local permissions. This allows us to "undo" all system added local ACEs without loosing user
   * provided permissions.
   *
   * @param identityId - the id of the user/group for the given permissions
   * @param permissions - the permissions
   */
  const syncOriginalLocalAces = (identityId: string, permissions: PermissionsV1) => {
    if (originalLocalAces !== null) {
      const existingOriginalLocalAce: any = _.find(originalLocalAces, {
        identityId,
      });
      if (existingOriginalLocalAce) {
        existingOriginalLocalAce.permissions = permissions;
      } else {
        originalLocalAces.push({ identityId, permissions });
      }
      setOriginalLocalAces([...originalLocalAces]);
    }
  };

  /**
   * Maps an API AceOutput to an ace entry used in the details pane
   */
  const createDetailsEntry: (entry: AceOutputV1, currentUserId: string) => DetailTableAce = (entry, currentUserId) => {
    const nameRedacted = t('ACCESS_CONTROL.REDACTED');
    let inheritedFrom: string;
    if (_.get(entry, 'origin.isRedacted', false)) {
      inheritedFrom = nameRedacted;
    } else {
      inheritedFrom = _.get(entry, 'origin.name', '');
    }
    return {
      id: entry.id,
      name: `${_.get(entry, 'identity.name')} ${getAdditionalText(entry, t, currentUserId)}`,
      permissions: entry.permissions ? prettyPermissions(entry.permissions) : '',
      inheritedFrom,
    };
  };

  /**
   * This function fetches the inherited acl data displayed on the details tab.
   */
  const fetchInheritedAcl = () => {
    setLoadingDetails(true);
    return getItemACLDetails(itemId, true)
      .then(({ entries }) => {
        setInheritedAcl(
          _.chain(entries)
            .filter('origin')
            .map((entry) => createDetailsEntry(entry, currentUser.id))
            .value(),
        );
      })
      .catch((ex) => {
        logError(formatMessage`Error fetching ACL details: ${ex}`);
      })
      .finally(() => setLoadingDetails(false));
  };

  /**
   * Sets the tabIndex that determines which tab is displayed.
   */
  const changeTab: (tabIndex: string) => void = (tabIndex) => {
    // We don't want to do anything if we are clicking the tab we are already on (CRAB-17339)
    if (currentTabIndex === tabIndex || loading) {
      return;
    }

    if (tabIndex === ADVANCED_TAB_INDEX) {
      doTrack(TRACK.ACL_MODAL, 'Details Tab');
      setDetailsEntries();
    } else if (tabIndex === LOCK_TAB_INDEX) {
      doTrack(TRACK.ACL_MODAL, 'Lock Tab');
    } else {
      doTrack(TRACK.ACL_MODAL, 'Manage Tab');
    }

    setCurrentTabIndex(tabIndex);
  };

  const setDetailsEntries = (acl = itemAcl) => {
    setDetailTableEntries(
      _.chain(acl.entries)
        .reject('origin')
        .filter((entry) => entry.permissions.read || entry.permissions.write || entry.permissions.manage)
        .map((entry) => createDetailsEntry(entry, currentUser.id))
        .concat(acl.permissionsInheritanceDisabled ? [] : inheritedAcl)
        .sortBy('name')
        .value(),
    );
  };

  const setIdentity = useCallback(
    (identity: Identity) => {
      if (identity.id) {
        setNewAce({ ...newAce, identity });
      } else {
        setNewAce(_.clone(DEFAULT_ACE));
      }
    },
    [newAce],
  );

  const titleIcon = (
    <ExternalLink href={`${SUPPORT_LINK}/authorization`} variant="icon">
      <Icon
        icon={faQuestionCircle}
        large={true}
        tooltip={t('ITEM_ACL.CLICK_FOR_INFO')}
        extraClassNames="text-interactive"
      />
    </ExternalLink>
  );

  const dialogClassName = () => {
    if (currentTabIndex === BASIC_TAB_INDEX) {
      return 'overflowVisible';
    }
    return '';
  };

  const canBeLocked = currentTabIndex === LOCK_TAB_INDEX && understandLockingIsPermanent;
  const canSaveACL = (currentTabIndex !== LOCK_TAB_INDEX && !aclIsModified(itemAcl, originalItemAcl)) || saving;

  const getSubmitButtonLabel = () => {
    if (currentTabIndex === LOCK_TAB_INDEX) {
      if (lockErrors) {
        return t('ACCESS_CONTROL.LOCKING.OK');
      }
      return t('ACCESS_CONTROL.LOCKING.CONFIRM_LOCK');
    }
    return t('SAVE');
  };

  const handleSubmit = async () => {
    if (currentTabIndex === LOCK_TAB_INDEX) {
      if (lockErrors && hasLockingFailedPreviouslyDueToGlobalItems) {
        dismissLockErrors();
        close();
      } else {
        await lockAndVersion(versionState);
      }
    } else {
      if (itemIsGloballyScoped && isGrantingAdditionalReadAccess(originalItemAcl?.entries, localAceEntries)) {
        setShowGlobalScopeAccessConfirmModal(true);
        return;
      }
      return save();
    }
  };

  const showSaveToast = (savedAces: AceOutputV1[]) => {
    successToast(
      getAclSaveToastMessage(savedAces, originalOrgIdentity.current, originalItemAcl?.localEntries, itemType),
    );
  };

  const aclModalTabs = [
    {
      label: userHasManagePermission ? t('ACCESS_CONTROL.MANAGE') : t('ACCESS_CONTROL.OVERVIEW'),
      id: BASIC_TAB_INDEX,
      tabContentExtraClassNames: 'tw:h-full',
      testId: `aclModalTabs-tab-${BASIC_TAB_INDEX}`,
      content: (
        <div data-testid={`aclModalTabs-tabpane-${BASIC_TAB_INDEX}`}>
          <AclModalMainTab
            isAclFromDatasource={isAclFromDatasource}
            addItemAce={addItemAce}
            newAce={newAce}
            loading={loading}
            saving={saving}
            displayAceEntries={displayAceEntries}
            currentUserId={currentUser.id}
            itemAcl={itemAcl}
            setIdentity={setIdentity}
            setPermissions={setPermissions}
            workbookId={workbookId || ''}
            worksheetId={_.includes(JOURNAL_OR_REPORT, item?.type) ? item.worksheetId : worksheetId}
            includeLinksAndCorporateMessage={includeLinksAndCorporateMessage}
            folderId={folderId}
            showOnlyFolderLinks={showOnlyFolderLinks}
            itemId={itemId}
            itemName={itemNameState || ''}
            isAclModalLocationInHeader={isAclModalLocationInHeader}
            isItemAncestorNotCorporate={isItemAncestorNotCorporate}
            item={item}
            aclModified={aclIsModified(itemAcl, originalItemAcl)}
            fetchInherited={updatedInheritedPermissionsDisplay}
            onClose={close}
            userHasManagePermission={userHasManagePermission}
            isVantage={isVantage}
            hasMultipleOrganizations={hasMultipleOrganizations}
            itemType={itemType}
          />
        </div>
      ),
    },
    {
      label: t('ACCESS_CONTROL.ADVANCED'),
      id: ADVANCED_TAB_INDEX,
      tabContentExtraClassNames: 'tw:h-full',
      testId: `aclModalTabs-tab-${ADVANCED_TAB_INDEX}`,
      content: (
        <div data-testid={`aclModalTabs-tabpane-${ADVANCED_TAB_INDEX}`}>
          <AclModalAdvancedTab
            loadingDetails={loadingDetails}
            detailTableEntries={detailTableEntries}
            togglePermissionsInheritance={togglePermissionsInheritance}
            hasManagePermission={userHasManagePermission}
            itemAcl={itemAcl}
            allowDisablingPermissionInheritance={!restrictPermissionsInheritance}
            workbookId={workbookId}
            itemId={itemId}
            item={item}
          />
        </div>
      ),
    },
    {
      label: t('ACCESS_CONTROL.CHECK_ACCESS'),
      id: CHECK_ACCESS_TAB_INDEX,
      testId: `aclModalTabs-tab-${CHECK_ACCESS_TAB_INDEX}`,
      content: (
        <div data-testid={`aclModalTabs-tabpane-${CHECK_ACCESS_TAB_INDEX}`}>
          <AclModalCheckAccessTab itemAcl={itemAcl} />
        </div>
      ),
    },
  ];

  if (
    (lockingAndVersioningAppliesToWorkbook || lockingAppliesToVantage) &&
    userHasManagePermission &&
    hasLockingFeature &&
    !onHomeScreen() &&
    !hideLockTab
  ) {
    aclModalTabs.push({
      label: t('ACCESS_CONTROL.LOCK_TAB_TITLE'),
      id: LOCK_TAB_INDEX,
      testId: `aclModalTabs-tab-${LOCK_TAB_INDEX}`,
      content: (
        <div data-testid={`aclModalTabs-tabpane-${LOCK_TAB_INDEX}`}>
          <AclModalLockTab
            isLocked={isLocked}
            lockedTimestamp={lockedTimestamp}
            lockedByDisplayName={lockedByDisplayName}
            userHasManagePermission={userHasManagePermission}
            hasLockingFailedPreviouslyDueToGlobalItems={hasLockingFailedPreviouslyDueToGlobalItems}
            checkboxHandler={toggleUnderstandLockingIsPermanent}
            understandLockingIsPermanent={understandLockingIsPermanent}
            versionState={versionState}
            includeVersioningSection={lockingAndVersioningAppliesToWorkbook}
            isVantage={isVantage}
          />
        </div>
      ),
    });
  }

  return (
    <Modal
      onClose={() => (saving ? setShowCloseDuringSaveModal(true) : close())}
      onSubmit={() => handleSubmit()}
      disableSubmitButton={
        canSaveACL ||
        (currentTabIndex === LOCK_TAB_INDEX && !canBeLocked) ||
        !!versionState.newWorkbookNameError ||
        hasMultipleOrganizations
      }
      open={true}
      size="4xl"
      testId="acl-modal"
      dialogClassName={dialogClassName()}
      title={t('ITEM_ACL.HEADER')}
      titleIcon={titleIcon}
      titleIconPosition="right"
      subtitle={itemNameState}
      submitButtonLabel={getSubmitButtonLabel()}
      hideSubmitButton={!userHasManagePermission}
      cancelButtonLabel={userHasManagePermission ? t('CANCEL') : t('CLOSE')}>
      <div className="tw:m-sq-10 tw:h-sq-500! tw:min-w-sq-800!">
        <Tabs
          extraClassNames="width-maximum tw:h-full"
          defaultActiveTab={BASIC_TAB_INDEX}
          transition={false}
          activeTab={currentTabIndex}
          onTabSelect={(key) => changeTab(key)}
          tabs={aclModalTabs}
          id="aclModalTabs"
        />
        {showUnsavedChangesModal && (
          <UnsavedChangesModal
            onCancel={() => setShowUnsavedChangesModal(false)}
            onDiscard={() => {
              setShowUnsavedChangesModal(false);
              close(true, true);
            }}
          />
        )}
        {showGlobalScopeAccessConfirmModal && (
          <GlobalScopeAccessConfirmModal
            onCancel={() => setShowGlobalScopeAccessConfirmModal(false)}
            onConfirm={() => {
              setShowGlobalScopeAccessConfirmModal(false);
              save();
            }}
          />
        )}
        {showCloseDuringSaveModal && (
          <ClosedWhileSavingModal
            onClose={() => {
              setShowCloseDuringSaveModal(false);
              close(true, true);
            }}
          />
        )}
      </div>
    </Modal>
  );
};
